import type { Document } from "../document/Document";
import type { GroupLayer, Layer } from "../document/Layer";
import type { Command } from "./Command";

// Move a layer (raster or group) to a new position in the tree.
//
// - targetParentId: null = root list
// - targetIndex: index within the destination list (bottom-first, like Document.layers)
//
// Captures the original parent + index in `apply` so `invert` can return it
// to where it came from.

interface Position {
  parentId: string | null;
  index: number;
}

export class ReorderLayerCommand implements Command {
  readonly kind = "reorder-layer";
  private from: Position | null = null;

  constructor(
    private readonly layerId: string,
    private readonly targetParentId: string | null,
    private readonly targetIndex: number,
  ) {}

  apply(doc: Document): Document {
    const found = findPosition(doc.layers, this.layerId, null);
    if (!found) return doc;
    this.from = { parentId: found.parentId, index: found.index };
    const removed = removeById(doc.layers, this.layerId);
    const inserted = insertAt(removed, found.layer, this.targetParentId, this.targetIndex);
    return { ...doc, layers: inserted };
  }

  invert(before: Document, _after: Document): Command {
    const from = this.from ?? (() => {
      const f = findPosition(before.layers, this.layerId, null);
      return f ? { parentId: f.parentId, index: f.index } : null;
    })();
    if (!from) return new ReorderLayerCommand(this.layerId, this.targetParentId, this.targetIndex);
    return new ReorderLayerCommand(this.layerId, from.parentId, from.index);
  }
}

interface Found {
  parentId: string | null;
  index: number;
  layer: Layer;
}

function findPosition(layers: readonly Layer[], id: string, parentId: string | null): Found | null {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.id === id) return { parentId, index: i, layer: l };
    if (l.kind === "group") {
      const inner = findPosition(l.children, id, l.id);
      if (inner) return inner;
    }
  }
  return null;
}

function removeById(layers: readonly Layer[], id: string): Layer[] {
  const out: Layer[] = [];
  for (const l of layers) {
    if (l.id === id) continue;
    if (l.kind === "group") {
      const group: GroupLayer = { ...l, children: removeById(l.children, id) };
      out.push(group);
    } else {
      out.push(l);
    }
  }
  return out;
}

function insertAt(
  layers: readonly Layer[],
  layer: Layer,
  parentId: string | null,
  index: number,
): Layer[] {
  if (parentId === null) {
    const clamped = Math.max(0, Math.min(index, layers.length));
    return [...layers.slice(0, clamped), layer, ...layers.slice(clamped)];
  }
  return layers.map((l) => {
    if (l.id === parentId && l.kind === "group") {
      const clamped = Math.max(0, Math.min(index, l.children.length));
      const group: GroupLayer = {
        ...l,
        children: [...l.children.slice(0, clamped), layer, ...l.children.slice(clamped)],
      };
      return group;
    }
    if (l.kind === "group") {
      return { ...l, children: insertAt(l.children, layer, parentId, index) };
    }
    return l;
  });
}
